import { GlobalConstants } from '../../global-constants';
import {
  exposeGitInfo,
  parseBuildInfo,
  type BuildInfo,
} from './git-info';

export interface BuildInfoState {
  readonly status: 'loading' | 'unavailable' | 'unknown';
}

export type ComponentBuildInfo = BuildInfo | BuildInfoState;

export interface RuntimeVersionInfo {
  readonly officialVersion: string;
  web: ComponentBuildInfo;
  api: ComponentBuildInfo;
  validator: ComponentBuildInfo;
}

interface HealthResponse {
  components?: {
    api?: unknown;
    validator?: unknown;
  };
}

declare global {
  // eslint-disable-next-line no-var
  var _version: RuntimeVersionInfo;
}

const loading = (): BuildInfoState => ({ status: 'loading' });

function exposeVersionInfo(info: RuntimeVersionInfo): void {
  Object.defineProperty(globalThis, '_version', {
    configurable: true,
    enumerable: false,
    get: () => info,
  });
}

async function loadWebInfo(
  info: RuntimeVersionInfo,
  uri: string
): Promise<void> {
  try {
    const response = await fetch(uri);
    if (!response.ok) {
      info.web = { status: 'unavailable' };
      return;
    }

    const value: unknown = await response.json();
    const webInfo = parseBuildInfo(value);
    if (!webInfo) {
      info.web = { status: 'unknown' };
      return;
    }

    exposeGitInfo(webInfo);
    info.web = webInfo;
  } catch {
    info.web = { status: 'unavailable' };
  }
}

async function loadBackendInfo(
  info: RuntimeVersionInfo,
  uri: string
): Promise<void> {
  try {
    const response = await fetch(uri);
    if (!response.ok) {
      info.api = { status: 'unavailable' };
      info.validator = { status: 'unavailable' };
      return;
    }

    const health = (await response.json()) as HealthResponse;
    info.api = parseBuildInfo(health.components?.api) ?? { status: 'unknown' };
    info.validator =
      parseBuildInfo(health.components?.validator) ?? { status: 'unknown' };
  } catch {
    info.api = { status: 'unavailable' };
    info.validator = { status: 'unavailable' };
  }
}

export function initializeVersionInfo(
  webUri = '/git-info.json',
  healthUri = '/api/health'
): RuntimeVersionInfo {
  const info: RuntimeVersionInfo = {
    officialVersion: GlobalConstants.appInfo.appVersion,
    web: loading(),
    api: loading(),
    validator: loading(),
  };

  exposeVersionInfo(info);
  void loadWebInfo(info, webUri);
  void loadBackendInfo(info, healthUri);

  return info;
}
